import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import LoadingOverlay from "react-loading-overlay-ts";
import _, { cloneDeep } from "lodash";
import { Toast } from "primereact/toast";
import PageLayout from "../../PageLayout";
import DesignHeader from "../DesignJobs/DesignHeader";
import FooterButtons from "../DesignJobs/FooterButtons";
import FullkitHeader from "./FullkitHeader";
import TaskSpecificInfo from "./TasksSpecificInfo";
import UnAuthorizedComponent from "../../UnAuthorized/UnAuthorizedComponent";
import { UnsavedChangesDialog, useUnsavedChangesWarning } from "../../common/NavigationPrompt";
import useAzureUploadFolderStructure from "../../../hooks/useAzureUploadFolderStructure";
import { getTaskDetails } from "../../../store/actions/taskDetailAction";
import { formatIntoDatePicker } from "../../Projects/ProjectPlan/util";
import { changeDateFormat, redirectToProjectPlanOrTaskPage } from "../../../utils";
import { totalFileSizeQuotient } from "../../../constants/FileUploadConstant";
import { submitCustomFullkitTask } from "../../../apis/customFullkitApi";
import { uploadtoAzureStrContainer } from "../../../apis/uploadAzuerContainer";
import { AzureContainerFail, AzureContainerSuccess } from "../../../Utils/constants";
import { getReadWriteAccessForTask, getUserAccessForTask } from "../../../Utils/helpers";

import "./index.scss";

function TaskList() {
  const [enableSubmit, setEnableSubmit] = useState(true);
  const [loader, setLoader] = useState(false);
  const [fullkitName, setFullkitName] = useState("");
  const [errorAlert, setErrorAlert] = useState(false);
  const [accessType, setAccessType] = useState("Write");
  const [accessSecurityMatrix, setUserAccess] = useState(true);
  const [checkReadWriteAccess, setCheckReadWriteAccess] = useState(true);
  const [pageEdited, setPageEdited] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedTaskList, setSelectedTaskList] = useState(null);
  const [taskDetails, setTaskDetails] = useState(null);
  const [initialTaskDetails, setInitialTaskDetails] = useState(null);
  const [taskStatus, setTaskStatus] = useState("");
  const [documentSequence, setDocumentSequence] = useState(2);

  const folderStructure = useAzureUploadFolderStructure("Custom Fullkit", 0, false);

  const toast = useRef();

  const { markDirty, markClean } = useUnsavedChangesWarning();

  const {
    userDetails: { AccessGroup, UserGroup },
  } = useSelector((state) => state?.UserDetailsReducer);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { page1, page2, pageType, TaskID, ProjectID } = useParams();

  const User = useSelector((state) => state.UserReducer);
  const { TaskDetailsData, loading } = useSelector((state) => state.TaskDetailsReducer);

  const headerName = "Custom Fullkit";
  const shortName = "Fullkit Element";

  const userInformation = User?.userInformation;
  const userAccessPayLoad = {
    ArtworkAgilityPage: {
      UserId: userInformation?.userid,
      ProjectID: ProjectID,
      UserGroup: UserGroup,
    },
  };

  useEffect(() => {
    markClean();
    setPageEdited(false);
    setLoader(true);
  }, []);

  useEffect(() => {
    setAccessType(TaskDetailsData?.AccessType);
    setUserAccess(getUserAccessForTask(TaskDetailsData));
    setCheckReadWriteAccess(getReadWriteAccessForTask(TaskDetailsData));
  }, [TaskDetailsData?.AccessType]);

  useEffect(() => {
    if (userInformation?.userid && UserGroup?.length) {
      dispatch(getTaskDetails(TaskID, ProjectID, userAccessPayLoad));
    }
  }, [dispatch, TaskID, ProjectID, userInformation?.userid, UserGroup?.length]);

  useEffect(() => {
    if (TaskDetailsData) {
      setFullkitName(TaskDetailsData?.ArtworkAgilityTasks[0]?.Task_Name);
      setTaskStatus(TaskDetailsData.ArtworkAgilityTasks[0].Task_Status);
      setSelectedTaskList(TaskDetailsData?.TaskList);

      const tempArr = TaskDetailsData?.ArtworkAgilityTasks[0]?.DesignJobDetails?.map((job) => ({
        checked: TaskDetailsData?.ArtworkAgilityTasks[0].Task_Status === "Complete",
        type: job?.Type,
        value: job?.Value,
        date: formatIntoDatePicker(changeDateFormat(job?.EndDate)),
        assignee: job?.Assignee,
        fileRequired: job?.IsFileUploadMandatory,
        file:
          job?.FileMetaDataList.length === 1 && job?.FileMetaDataList[0].File_Name === ""
            ? addNewFiles(true)
            : job?.FileMetaDataList,
        note: job?.Additional_Info,
      }));

      setDocumentSequence(TaskDetailsData?.ArtworkAgilityTasks[0]?.DesignJobDetails?.length + 1);
      if (tempArr) {
        setTaskDetails(...tempArr);
        setInitialTaskDetails(...tempArr);
      }
      setLoader(false);
    }
  }, [TaskDetailsData]);

  useEffect(() => {
    setEnableSubmit(taskStatus === "Complete" ? false : getSubmitDisableState());

    if (JSON.stringify(taskDetails) === JSON.stringify(initialTaskDetails)) {
      markClean();
      setPageEdited(false);
    } else {
      setPageEdited(true);
      markDirty();
    }
  }, [taskDetails]);

  const getSubmitDisableState = () => {
    if(taskDetails?.fileRequired && taskDetails?.checked) {
      return taskDetails?.file?.some(fileObj => fileObj.File_Name !== "")
    }
    return taskDetails?.checked
  }

  const handleDone = () => {
    setTaskDetails((prevState) => ({
      ...prevState,
      checked: !prevState.checked,
    }));
  };

  const handleCancelClick = () => {
    if (pageEdited) {
      setShowDialog(true);
    } else {
      redirectToProjectPlanOrTaskPage(page1, page2, pageType, ProjectID, navigate)
    }
  };

  // it will hide warning dialog
  const cancelNavigation = () => setShowDialog(false);

  // it will navigate to previously saved url navigate(pendingNavigation);
  // by doing setShowDialog(false) and setPendingNavigation("");
  const confirmNavigation = () => {
    markClean();
    redirectToProjectPlanOrTaskPage(page1, page2, pageType, ProjectID, navigate)
  };

  const handleProjectNameClick = () => {
    if (pageEdited) {
      setShowDialog(true);
    } else {
      handleProjectName();
    }
  };

  const handleProjectName = () => {
    if (page2 && page2.toLowerCase() === "projectplan") {
      return navigate(`/${page1}/${page2}/${ProjectID}`);
    } else if (pageType) {
      if (pageType.toLowerCase() === "mytasks") {
        navigate(`/myProjects/projectPlan/${ProjectID}`);
      } else if (pageType.toLowerCase() === "alltasks") {
        navigate(`/allProjects/projectPlan/${ProjectID}`);
      }
    }
  };

  const addNewFiles = (isNew = false) => {
    if (isNew) {
      return [
        {
          isNew: true,
          File_Name: "",
          Sequence: 1,
          Version: 1,
          Email: "",
          FileID: "",
          File_Path: "",
          Full_Name: "",
          JobID: "",
          JobName: "",
          ReadOnlyURL: "",
          Timestamp: "",
          TypeOfFile: "",
        },
      ];
    }

    const clonedTasks = _.cloneDeep(taskDetails?.file);

    clonedTasks.push({
      isNew: true,
      File_Name: "",
      Sequence: documentSequence,
      Version: 1,
      Email: "",
      FileID: "",
      File_Path: "",
      Full_Name: "",
      JobID: "",
      JobName: "",
      ReadOnlyURL: "",
      Timestamp: "",
      TypeOfFile: "",
    });

    setDocumentSequence((prevState) => prevState + 1);
    setTaskDetails((prev) => ({ ...prev, file: clonedTasks }));
  };

  const orderBySequence = (arr) => {
    return arr.sort(function (a, b) {
      return a.Sequence - b.Sequence;
    });
  };

  const onFileUpload = (e, FileDetails, fileUploadSection, seq) => {
    const temp = cloneDeep(taskDetails?.file);
    temp.forEach((obj, i) => {
      if (seq === obj?.Sequence) {
        obj["File_Name"] = e?.files?.[0]?.name;
        if (e?.files?.[0]?.name !== "") {
          obj["Info"] = {
            fileInfo: e?.files?.[0],
            uploadType: "",
            sequence: seq,
            version: FileDetails?.Version,
            id: i,
            Full_Name:
              FileDetails?.Info === undefined
                ? FileDetails?.Full_Name
                : FileDetails?.Info.Full_Name,
            Email: FileDetails?.Info === undefined ? FileDetails?.Email : FileDetails?.Info.Email,
          };
          obj["AzureFile"] =
            FileDetails?.file !== undefined ? FileDetails?.file : FileDetails?.AzureFile;
          obj["Timestamp"] = FileDetails?.Timestamp;
        } else {
          obj["Info"] = "";
          obj["AzureFile"] = "";
        }
      }
    });

    setTaskDetails((prev) => {
      return {
        ...prev,
        file: orderBySequence(temp),
      };
    });
  };

  const onFileClear = (fileUploadSection, fileListToRemove) => {
    const newFileList = taskDetails?.file.filter(
      (fileList) => fileList?.Sequence !== fileListToRemove?.Sequence
    );
    setTaskDetails((prevState) => ({ ...prevState, file: newFileList }));
  };

  const handleNoteChange = (e) => {
    setTaskDetails((prevState) => ({ ...prevState, note: e.target.value }));
  };

  const getFilesListObj = () => {
    let newFileList = [];
    taskDetails.file.map(async (obj) => {
      if ("AzureFile" in obj) {
        if (obj.Info) {
          const fileSize = Math.round(obj?.Info?.fileInfo?.size / totalFileSizeQuotient);
          const temp = {
            Version: obj.Info.version,
            Size: fileSize === 0 ? "1" : fileSize,
            Sequence: obj?.Sequence,
            Filename: obj.File_Name,
            Full_Name: obj?.Info?.Full_Name,
            Email: obj?.Info?.Email,
            TypeOfFile: obj?.File_Name?.split(".")?.pop(),
            JobID: TaskDetailsData.ArtworkAgilityTasks[0].DesignJobDetails[0].Design_Job_ID,
            JobName: `${taskDetails.type}_FK_${taskDetails.value}`,
          };
          newFileList.push(temp);
          let azureRes = await uploadtoAzureStrContainer(
            obj?.file === undefined ? obj.AzureFile : obj.file,
            `${folderStructure}/${obj.Info.version}/`
          );
          if (azureRes) {
            if (toast.current) {
              toast.current.show(AzureContainerSuccess);
            }
          } else {
            if (toast.current) {
              toast.current.show(AzureContainerFail);
            }
          }
        }
      }
    });
    return newFileList;
  };

  const onSubmit = async () => {
    setLoader(true);
    const submitFullkitTaskFiles = getFilesListObj();

    const payload = {
      caseTypeID: "PG-AAS-Work-CustomFullkitConfirmTask",
      content: {
        AWMTaskID: TaskID,
        AWMProjectID: ProjectID,
        AdditionalInfo: taskDetails.note,
        Parent_AWM_Task_ID: TaskDetailsData.ArtworkAgilityTasks[0].AWM_Task_ID,
      },
      pageInstructions: submitFullkitTaskFiles.map((fileItem) => ({
        instruction: "APPEND",
        target: "CFKCFileList",
        content: fileItem,
      })),
    };

    const res = await submitCustomFullkitTask(
      payload,
      TaskDetailsData?.ArtworkAgilityTasks[0]?.Task_Key,
      TaskDetailsData?.ArtworkAgilityPage?.Etag
    );

    if (res.status === 200) {
      setLoader(false);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Custom Fullkit task submitted",
        life: 3000,
      });

      setTimeout(() => redirectToProjectPlanOrTaskPage(page1, page2, pageType, ProjectID, navigate), 4000);
    } else {
      setLoader(false);
      toast.current.show({
        severity: "error",
        detail: res?.statusText,
        life: 3000,
      });
    }
  };

  return (
    <PageLayout>
      <Toast ref={toast} />
      {accessSecurityMatrix ? (
        <LoadingOverlay active={loader || loading} className="overlay-wrapper" spinner text="">
          <DesignHeader
            headerName={headerName}
            label={shortName}
            lastLabel={"Custom Fullkit Task"}
            pageEdited={pageEdited}
            setPageEdited={setPageEdited}
            checkReadWriteAccess={checkReadWriteAccess}
            actionButtonsFlag={true}
          />
          <FullkitHeader
            isEditMode={false}
            checkReadWriteAccess={checkReadWriteAccess}
            fullkitName={fullkitName}
            setFullkitName={setFullkitName}
            errorAlert={errorAlert}
            setErrorAlert={setErrorAlert}
            isTaskPage={true}
            taskListOptions={TaskDetailsData?.TaskList}
            selectedTaskList={selectedTaskList}
            handleProjectNameClick={handleProjectNameClick}
          />
          <div className="fullkit-job-details">
            <TaskSpecificInfo
              checkReadWriteAccess={checkReadWriteAccess}
              pageEdited={pageEdited}
              setPageEdited={setPageEdited}
              taskDetail={taskDetails}
              setDone={() => handleDone()}
              taskStatus={taskStatus}
              addNewFiles={addNewFiles}
              onFileUpload={onFileUpload}
              onFileClear={onFileClear}
              onNoteChange={handleNoteChange}
            />
          </div>
          <FooterButtons
            accessType={accessType}
            handleCancel={handleCancelClick}
            onSubmit={onSubmit}
            formValid={pageEdited ? !enableSubmit : true}
            bottomFixed={true}
            hideSaveButton={true}
          />
        </LoadingOverlay>
      ) : (
        AccessGroup && AccessGroup.length && <UnAuthorizedComponent nowrapper={true} />
      )}
      {/* UnsavedChangesDialog popup*/}
      <UnsavedChangesDialog
        visible={showDialog}
        onCancel={cancelNavigation}
        onConfirm={confirmNavigation}
      />
    </PageLayout>
  );
}

export default TaskList;
